import { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Link2, X } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { ActionButton } from "./common";
import { RequesterCombobox, RequesterContactPanel } from "./RequesterPickerControls";

export const RequesterPicker = ({ customerId, value, snapshotName, onChange, testId }) => {
  const [records, setRecords] = useState([]);
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [unassignedId, setUnassignedId] = useState("");
  const [linkSearch, setLinkSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);
  const currentCustomer = useRef(customerId);
  currentCustomer.current = customerId;

  useEffect(() => {
    let current = true;
    setOpen(false); setPanel(null); setError(""); setRecords([]);
    if (!customerId) return () => { current = false; };
    setLoading(true);
    api.get("/requesters", { params: { active: true } }).then(({ data }) => {
      if (current) setRecords(data);
    }).catch((e) => { if (current) setError(errMsg(e)); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [customerId, reload]);

  const assigned = useMemo(() => records.filter((r) => r.customer_id === customerId), [records, customerId]);
  const unassignedOptions = useMemo(() => records
    .filter((r) => !r.customer_id && r.name.toLowerCase().includes(linkSearch.trim().toLowerCase()))
    .map((r) => ({ value: r.id, label: r.name })), [records, linkSearch]);
  const choose = (r) => { onChange(r); setOpen(false); setPanel(null); setError(""); };
  const openPanel = (type) => { setPanel(type); setName(""); setPhone(""); setUnassignedId(""); setLinkSearch(""); setError(""); };
  const saveContact = async () => {
    if (busy) return;
    if (panel === "add" && !name.trim()) { setError("Enter a requester name."); return; }
    if (panel === "link" && !unassignedId) { setError("Choose an unassigned requester."); return; }
    setBusy(true); setError("");
    try {
      const { data } = panel === "add"
        ? await api.post("/requesters", { name: name.trim(), phone: phone.trim() || null, customer_id: customerId })
        : await api.post(`/requesters/${unassignedId}/assign`, { customer_id: customerId });
      if (currentCustomer.current !== customerId) return;
      setRecords((prev) => [...prev.filter((r) => r.id !== data.id), data].sort((a, b) => a.name.localeCompare(b.name)));
      choose(data);
    } catch (e) { setError(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`${testId}-requester-field`} className="space-y-2 min-w-0">
      <label htmlFor={`${testId}-requester-trigger`} className="block text-xs font-mono uppercase tracking-[0.15em] text-slate-500">Requester</label>
      <div className="flex gap-2 min-w-0">
        <RequesterCombobox open={open} setOpen={setOpen} customerId={customerId} loading={loading} busy={busy} snapshotName={snapshotName} assigned={assigned} value={value} choose={choose} testId={testId} />
        {(value || snapshotName) && <button type="button" data-testid={`${testId}-requester-clear`} aria-label="Clear requester" disabled={busy} onClick={() => choose(null)} className="min-w-11 min-h-11 flex items-center justify-center rounded-md border border-slate-700 hover:bg-slate-800 transition-colors"><X size={16} /></button>}
      </div>
      {snapshotName && !value && <p data-testid={`${testId}-requester-legacy`} className="text-xs text-slate-500">Saved ticket text. Select a contact to link it, or leave it unchanged.</p>}
      {customerId && <div className="flex flex-wrap gap-2">
        <ActionButton type="button" testId={`${testId}-requester-add`} variant="secondary" disabled={busy} onClick={() => openPanel("add")} className="!text-xs min-h-11"><Plus size={14} /> Add Requester</ActionButton>
        <ActionButton type="button" testId={`${testId}-requester-link`} variant="secondary" disabled={busy || loading} onClick={() => openPanel("link")} className="!text-xs min-h-11"><Link2 size={14} /> Link Unassigned</ActionButton>
      </div>}
      <RequesterContactPanel panel={panel} name={name} setName={setName} phone={phone} setPhone={setPhone} linkSearch={linkSearch} setLinkSearch={setLinkSearch} unassignedId={unassignedId} setUnassignedId={setUnassignedId} unassignedOptions={unassignedOptions} busy={busy} saveContact={saveContact} cancel={() => { setPanel(null); setError(""); }} testId={testId} />
      {error && <div role="alert" data-testid={`${testId}-requester-error`} className="text-xs text-red-600 space-y-2"><p>{error}</p>{!panel && <button type="button" data-testid={`${testId}-requester-retry`} className="underline" onClick={() => setReload((n) => n + 1)}>Reload requesters</button>}</div>}
    </div>
  );
};
